// ============================================================================
// Componente: RHAutomacoes.js
// Objetivo: Central de automações dentro do Módulo de RH
// Padrão visual: RH / Vermelho (espelha FinanceiroAutomacoes.js)
// ============================================================================

import React, { useState } from 'react';
import {
    ArrowRight,
    Lock,
    ShieldCheck,
    FileSpreadsheet,
    Landmark,
    CheckCircle2,
    Send,
    CreditCard,
    QrCode,
    Heart,
    Bus
} from 'lucide-react';

import AutomacaoDirfEsocial from './RHAutomacaoDirfEsocial';
import AutomacaoBeneficioSocial from './RHAutomacaoBeneficioSocial';
import AutomacaoEmprestimo from './RHAutomacaoEmprestimo';
import AutomacaoValeTransporte from './RHAutomacaoValeTransporte';
import AutomacaoiFoodBeneficios from './RHAutomacaoiFoodBeneficios';
import AutomacaoVR from './RHAutomacaoVR';
import AutomacaoWellhub from './RHAutomacaoWellhub';
import { perfilNoModulo } from '../../../utils/perfisDerivados';

const RHAutomacoes = () => {
    const [activeSubTab, setActiveSubTab] = useState('visao-geral');

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const perfilUsuario = perfilNoModulo(user?.perfil, 'rh') || '';

    const canAcessarRH = [
        'RH',
        'Gerente Adm',
        'Admin',
        'Diretoria'
    ].includes(perfilUsuario);

    const corPrincipal = '#e11d48';
    const corPrincipalEscura = '#be123c';
    const corFundoClaro = '#fee2e2';
    const corFundoMaisClaro = '#fff1f2';

    const tabStyle = (isActive) => ({
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 18px',
        backgroundColor: isActive ? corFundoClaro : 'transparent',
        color: isActive ? corPrincipal : '#64748b',
        border: isActive ? `1px solid ${corPrincipal}` : '1px solid transparent',
        borderRadius: '8px',
        fontWeight: '700',
        cursor: 'pointer',
        transition: 'all 0.2s',
        fontSize: '14px'
    });

    // Catálogo de automações de RH (id, rótulo, ícone do menu e card)
    const AUTOMACOES = [
        {
            id: 'dirf',
            label: 'DIRF x eSocial',
            tab: <Send size={18} />,
            title: 'Análise DIRF vs eSocial — Plano Saúde',
            tag: 'Plano Saúde',
            desc: 'Cruzamento das bases Total, Titulares e Dependentes para gerar relatórios de diferenças e status.',
            icon: <ShieldCheck size={30} />,
            Componente: AutomacaoDirfEsocial
        },
        {
            id: 'beneficio',
            label: 'Benefício Social',
            tab: <FileSpreadsheet size={18} />,
            title: 'Benefício Social',
            tag: 'Excel 97-2003',
            desc: 'Extrai, trata e exporta dados de funcionários a partir de planilhas no formato legado .xls (97-2003).',
            icon: <FileSpreadsheet size={30} />,
            Componente: AutomacaoBeneficioSocial
        },
        {
            id: 'emprestimo',
            label: 'Empréstimo Consignado',
            tab: <Landmark size={18} />,
            title: 'Empréstimo Consignado',
            tag: 'Folha',
            desc: 'Automatiza a conferência da folha de pagamento referente a empréstimos consignados.',
            icon: <Landmark size={30} />,
            Componente: AutomacaoEmprestimo
        },
        {
            id: 'vt',
            label: 'Vale Transporte',
            tab: <Bus size={18} />,
            title: 'GvBus — Vale Transporte',
            tag: 'GvBus',
            desc: 'Automatiza o crédito do vale transporte cruzando o relatório da GvBus com o Populis (Sistema RH).',
            icon: (
                <img
                    src="/gvbus-logo.png"
                    alt="GvBus"
                    style={{ width: '38px', height: '38px', objectFit: 'contain' }}
                />
            ),
            Componente: AutomacaoValeTransporte
        },
        {
            id: 'ifood',
            label: 'iFood Benefícios',
            tab: <Heart size={18} />,
            title: 'iFood Benefícios',
            tag: 'Centro de Custo',
            desc: 'Separa automaticamente em relatórios individuais por Centro de Custo.',
            icon: (
                <img
                    src="/ifood-logo.png"
                    alt="iFood"
                    style={{ width: '34px', height: '38px', objectFit: 'contain' }}
                />
            ),
            Componente: AutomacaoiFoodBeneficios
        },
        {
            id: 'vr',
            label: 'VR',
            tab: <CreditCard size={18} />,
            title: 'VR — Vale Refeição',
            tag: 'VR',
            desc: 'Automatiza o crédito do VR cruzando o relatório da VR com o Populis (Sistema RH).',
            icon: (
                <img
                    src="/vr-logo.png"
                    alt="VR"
                    style={{ width: '38px', height: '38px', objectFit: 'contain' }}
                />
            ),
            Componente: AutomacaoVR
        },
        {
            id: 'wellhub',
            label: 'Wellhub',
            tab: <QrCode size={18} />,
            title: 'Wellhub',
            tag: 'Adição / Remoção',
            desc: 'Cruza a base ativa do Wellhub com o sistema para gerar as planilhas de Adição e Remoção.',
            icon: (
                <img
                    src="/wellhub-logo.png"
                    alt="Wellhub"
                    style={{ width: '38px', height: '38px', objectFit: 'contain' }}
                />
            ),
            Componente: AutomacaoWellhub
        }
    ];

    const mudarAba = (aba, permitido = true) => {
        if (!permitido) return;
        setActiveSubTab(aba);
    };

    const renderSubTab = () => {
        const item = AUTOMACOES.find((a) => a.id === activeSubTab);
        if (!item) return <VisaoGeralAutomacoes />;
        const Componente = item.Componente;
        return <Componente embedded />;
    };

    const AutomationCard = ({ id, title, desc, icon, tag, isAllowed = true, disabled = false }) => {
        const permitido = isAllowed && !disabled;

        return (
            <div
                onClick={() => mudarAba(id, permitido)}
                style={{
                    backgroundColor: permitido ? '#fff' : '#f8fafc',
                    border: '1px solid #f0e2e2',
                    borderRadius: '16px',
                    padding: '24px',
                    cursor: permitido ? 'pointer' : 'not-allowed',
                    opacity: permitido ? 1 : 0.65,
                    minHeight: '220px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.04)',
                    transition: 'all 0.2s ease-in-out'
                }}
                onMouseEnter={(e) => {
                    if (!permitido) return;
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 12px 20px -8px rgba(225, 29, 72, 0.35)';
                    e.currentTarget.style.borderColor = corPrincipal;
                }}
                onMouseLeave={(e) => {
                    if (!permitido) return;
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.04)';
                    e.currentTarget.style.borderColor = '#f0e2e2';
                }}
            >
                <div style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: '4px',
                    backgroundColor: permitido ? corPrincipal : '#cbd5e1'
                }} />

                {!permitido && (
                    <div style={{ position: 'absolute', top: '16px', right: '16px', color: '#94a3b8' }}>
                        <Lock size={20} />
                    </div>
                )}

                <div>
                    <div style={{
                        width: '52px',
                        height: '52px',
                        borderRadius: '14px',
                        backgroundColor: permitido ? corFundoMaisClaro : '#f1f5f9',
                        color: permitido ? corPrincipal : '#94a3b8',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '18px'
                    }}>
                        {icon}
                    </div>

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '8px',
                        flexWrap: 'wrap'
                    }}>
                        <h3 style={{ margin: 0, fontSize: '19px', fontWeight: '800', color: '#0f172a' }}>
                            {title}
                        </h3>

                        {tag && (
                            <span style={{
                                fontSize: '11px',
                                fontWeight: '800',
                                color: corPrincipalEscura,
                                backgroundColor: corFundoClaro,
                                borderRadius: '999px',
                                padding: '4px 8px'
                            }}>
                                {tag}
                            </span>
                        )}
                    </div>

                    <p style={{ margin: 0, color: '#64748b', fontSize: '14px', lineHeight: '1.6' }}>
                        {desc}
                    </p>
                </div>

                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginTop: '22px',
                    color: permitido ? corPrincipal : '#94a3b8',
                    fontSize: '14px',
                    fontWeight: '800'
                }}>
                    {permitido ? (
                        <>
                            <ArrowRight size={16} />
                            Acessar automação
                        </>
                    ) : (
                        <>
                            <Lock size={16} />
                            {disabled ? 'Em implantação' : 'Acesso bloqueado'}
                        </>
                    )}
                </div>
            </div>
        );
    };

    const VisaoGeralAutomacoes = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                gap: '20px'
            }}>
                {AUTOMACOES.map((a) => (
                    <AutomationCard
                        key={a.id}
                        id={a.id}
                        title={a.title}
                        tag={a.tag}
                        desc={a.desc}
                        icon={a.icon}
                        isAllowed={canAcessarRH}
                    />
                ))}
            </div>
        </div>
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '12px',
                borderBottom: '1px solid #f0e2e2',
                paddingBottom: '16px'
            }}>
                <button
                    onClick={() => setActiveSubTab('visao-geral')}
                    style={tabStyle(activeSubTab === 'visao-geral')}
                >
                    <CheckCircle2 size={18} />
                    Visão Geral
                </button>

                {AUTOMACOES.map((a) => (
                    <button
                        key={a.id}
                        onClick={() => setActiveSubTab(a.id)}
                        style={tabStyle(activeSubTab === a.id)}
                    >
                        {a.tab}
                        {a.label}
                    </button>
                ))}
            </div>

            <div style={{ paddingTop: '8px' }}>
                {renderSubTab()}
            </div>
        </div>
    );
};

export default RHAutomacoes;
